/**
 * @file useContentEditing.ts
 * @description Custom hook for managing content editing state.
 * Provides functionality to start editing, cancel editing, and update edited data.
 * Used to handle the editing state of generated SEO content in the application.
 */

import { useState, useCallback, useEffect } from "react";
import { ContentDebugInfo } from "../types";

interface EditableContentData {
  title: string;
  metaDescription: string;
  content: string;
  debugInfo?: ContentDebugInfo;
}

export const useContentEditing = (initialData: EditableContentData) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] =
    useState<EditableContentData>(initialData);

  useEffect(() => {
    if (!isEditing) {
      setEditedData(initialData);
    }
  }, [initialData, isEditing]);

  const startEditing = useCallback(() => {
    setIsEditing(true);
    setEditedData(initialData);
  }, [initialData]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditedData(initialData);
  }, [initialData]);

  const updateEditedData = useCallback(
    (newData: Partial<EditableContentData>) => {
      setEditedData((prevData) => ({ ...prevData, ...newData }));
    },
    []
  );

  return {
    isEditing,
    editedData,
    startEditing,
    cancelEditing,
    updateEditedData,
  };
};
